// Professional ForgotPassword.tsx with background videos
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../helpers/Supabase';
import logo from '../../../assets/vivvy-logo.png';
import '../../../styles/components/Login.scss';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(1);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Background video rotation
  useEffect(() => {
    const videoInterval = setInterval(() => {
      setCurrentVideo(prev => prev === 4 ? 1 : prev + 1);
    }, 8000); // Change video every 8 seconds

    return () => clearInterval(videoInterval);
  }, []);

  // Video transition effect
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.style.opacity = '0';
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.style.opacity = '1';
        }
      }, 500);
    }
  }, [currentVideo]);

  // Floating particles effect
  useEffect(() => {
    const createParticle = () => {
      const particle = document.createElement('div');
      particle.className = 'particle';
      particle.style.left = Math.random() * 100 + '%';
      particle.style.top = Math.random() * 100 + '%';
      particle.style.width = Math.random() * 3 + 1 + 'px';
      particle.style.height = particle.style.width;
      particle.style.animationDelay = Math.random() * 6 + 's';
      particle.style.animationDuration = (Math.random() * 4 + 4) + 's';
      
      const container = document.querySelector('.login-container');
      if (container) {
        container.appendChild(particle);
        
        // Remove particle after animation
        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
          }
        }, 12000);
      }
    };

    // Create particles periodically
    const particleInterval = setInterval(createParticle, 3000);
    
    // Create initial particles
    for (let i = 0; i < 8; i++) {
      setTimeout(createParticle, i * 300);
    }

    return () => clearInterval(particleInterval);
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(error.message);
      } else {
        setIsSubmitted(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Background Videos */}
      <div className="background-videos">
        <video
          ref={videoRef}
          className={`background-video ${currentVideo === 1 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-1.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 2 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-2.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 3 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-3.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 4 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-4.mp4')}
        />
        <div className="video-overlay"></div>
      </div>

      {/* Floating particles container */}
      <div className="floating-particles"></div>
      
      {/* Professional Content */}
      <div className="login-content">
        <div className="brand-section">
          <img src={logo} alt="Vivvy Logo" className="brand-logo" />
          <div className="brand-text">
            <h1>Reset Password</h1>
            <p>We'll help you get back to your account</p>
          </div>
        </div>

        <div className="form-section">
          <div className="form-container">
            {isSubmitted ? (
              <div className="success-section">
                <div className="success-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22,4 12,14.01 9,11.01"/>
                  </svg>
                </div>
                <h2>Check Your Email</h2>
                <p>
                  We've sent a password reset link to <strong>{email}</strong>
                </p>
                <p className="email-note">
                  Click the link in your email to reset your password.
                </p>
              </div>
            ) : (
              <>
                <div className="form-header">
                  <h2>Forgot Password?</h2>
                  <p>Enter your email to receive a reset link</p>
                </div>

                <form className="forgot-form" onSubmit={handleSubmit}>
                  <div className="input-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                  <button type="submit" className="submit-button" disabled={loading}>
                    {loading ? (
                      <>
                        <div className="loading-spinner"></div>
                        Sending Reset Link...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                  {error && <div className="error-message">{error}</div>}
                </form>
              </>
            )}

            <div className="form-footer">
              <Link to="/" className="back-link">← Back to Login</Link>
              <p>
                Don't have an account? <Link to="/onboarding" className="signup-link">Sign Up</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
